import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import uuid from 'uuid/v1';
import { useCurrentValueGetter, useNavigate } from 'hooks';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { handleSocialDataCallback } from '../../campaigns/modules/creative-display/utils/importFromSocialDataHandler';
import { WebSocketContext } from './component/WebSocketContext';
import { useWebSocketConnection } from './hooks/useWebSocketConnection';
import { subscribeToWebSocket } from './utils/utils';

const STATIC_LISTENERS = [
    {
        id: uuid(),
        callback: (message, { dispatch, navigate, account }) =>
            handleSocialDataCallback(message, { dispatch, navigate, account }),
        filter: data => !!data?.displayData,
    },
];

export const WebSocketProvider = ({ children }) => {
    const { sendMessage, onMessage, connected, __onMessage_ONLY_FOR_DEV_PURPOSES } = useWebSocketConnection();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const account = useAccount();
    const appDataGetter = useCurrentValueGetter({ dispatch, navigate, account });

    const value = useMemo(
        () => ({ sendMessage, onMessage, connected, __onMessage_ONLY_FOR_DEV_PURPOSES }),
        [sendMessage, onMessage, connected, __onMessage_ONLY_FOR_DEV_PURPOSES]
    );

    useEffect(() => {
        const unsubscribeFns = STATIC_LISTENERS.map(listener =>
            subscribeToWebSocket(onMessage, (...args) => listener.callback(...args, appDataGetter()), {
                filter: listener.filter,
                id: listener.id,
            })
        );

        return () => {
            unsubscribeFns.forEach(unsubscribe => unsubscribe());
        };
    }, [onMessage, appDataGetter]);

    return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
